import React, { useState, useEffect, useContext, useRef } from "react";
import { getDisplayError } from "../../utils/displayError";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  ChevronRight,
  Gift,
  Bell,
} from "lucide-react";
import { apiFetch } from "../../context/AuthContext";
import { useTheme } from "../../constant/theme";
import { useProfile } from "../../hooks/useProfile";

// ===== LANGUAGE CONTEXT =====
import { LanguageContext } from "../../constant/LanguageContext";

// ===== HOOKS =====
import useResponsive from "./hooks/useResponsive";
import useLeaveData, { getInfinityCode, normalizeInfinityCodeValue } from "./hooks/useLeaveData";

// ===== COMPONENTS =====
import LeaveTypeCard from "./components/LeaveTypeCard";
import HolidayCard from "./components/HolidayCard";
import LeaveHistoryTab from "./components/LeaveHistoryTab";
import TeamLeaveCalendar from "./components/TeamLeaveCalendar";
import RequestModal from "./components/RequestModal";
import DetailsModal from "./components/DetailsModal";
import AlertModal from "../../components/AlertModal";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";

// ===== CONSTANTS =====
import { translations } from "./constants/translations";

// ===== STYLES =====
import {
  getHeaderStyle,
  getBtnPrimary,
  getTabButtonStyle,
  getToastStyle,
  getLeaveTypesGridStyle,
} from "./styles/styles";

const apiBaseURL = process.env.REACT_APP_BASE_URL;

// ===== TABS =====
// الـ tabs الأربعة: نظرة عامة، السجل، الفريق، الإجازات الرسمية
const TABS = ["overview", "history", "team", "holidays"];

// ===== MAIN COMPONENT =====
const LeavePage = ({ employeeData }) => {
  const { screenSize, spacing, fonts, container } = useResponsive();
  const { isDarkMode, currentTheme: theme } = useTheme();

  // ✅ نجيب اللغة من LanguageContext مباشرةً (مش من props)
  // هيعمل react re-render أوتوماتيك لما اللغة تتغير في أي مكان في التطبيق
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations["en"];
  const { profile } = useProfile();

  // ─── Data hook ───────────────────────────────────────────────────────────────
  const {
    leaveTypes,
    leaveStats,
    leaveHistory,
    holidays,
    isLoading,
    errorMessage,
    successMessage,
    employeeImages,
    setErrorMessage,
    setSuccessMessage,
    fetchAllData,
    fetchHolidays,
    setLeaveHistory,
  } = useLeaveData({ employeeData, profile, theme, language, t });

  // ─── Validate leaveTypes (تأكد إن كل نوع إجازة عنده icon صح) ────────────────
  const validLeaveTypes = React.useMemo(
    () =>
      (Array.isArray(leaveTypes) ? leaveTypes : []).filter((lt) => {
        if (!lt || typeof lt !== "object") return false;
        if (!lt.id || !lt.icon || typeof lt.icon !== "function") {
          if (process.env.NODE_ENV !== "production") {
            console.warn("[LeavePage] leaveType غير صالح:", { lt });
          }
          return false;
        }
        return true;
      }),
    [leaveTypes]
  );

  // ─── Tab state ───────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("overview");

  // ─── Modal state ─────────────────────────────────────────────────────────────
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [expandedLeaveId, setExpandedLeaveId] = useState(null);

  // ─── Form state ──────────────────────────────────────────────────────────────
  const [isPartialDayMode, setIsPartialDayMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    type: "",
    startDate: "",
    endDate: "",
    reason: "",
    handover: "",
  });
  const [partialDayForm, setPartialDayForm] = useState({
    date: new Date().toISOString().split("T")[0],
    halfDayType: "",
    reason: "",
  });

  // ─── الروشتة الطبية (إجازة مرضية فقط) ────────────────────────────────────────
  // The file is uploaded first, then its returned path travels with the
  // leave-create payload — the backend rejects sick leave without it.
  const [prescription, setPrescription] = useState(null); // { path, fileName }
  const [isUploadingPrescription, setIsUploadingPrescription] = useState(false);
  const [prescriptionError, setPrescriptionError] = useState("");

  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "info" });
  const showAlert = (title, message, variant = "info") =>
    setAlertModal({ open: true, title, message, variant });
  const closeAlert = () => setAlertModal((p) => ({ ...p, open: false }));

  // ─── Manager code (للتقويم الجماعي) ──────────────────────────────────────────
  // The team-calendar API expects the MANAGER's infinity code, not the employee's own.
  // It queries: employees WHERE firstLineManagerInfinityCode = :managerCode
  // Passing the employee's own code returns no team (they have no direct reports).
  const managerInfinityCode =
    normalizeInfinityCodeValue(getInfinityCode(employeeData)) ||
    normalizeInfinityCodeValue(profile?.infinityCode) ||
    normalizeInfinityCodeValue(localStorage.getItem("infinityCode"));

  // ─── جلب البيانات عند أول تحميل ──────────────────────────────────────────────
  useEffect(() => {
    fetchAllData();
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Skeleton فقط في أول تحميل — مش هيظهر تاني مع كل ريفريش ─────────────────
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!isLoading) setPageReady(true);
  }, [isLoading]);

  // ─── رفع الروشتة الطبية ──────────────────────────────────────────────────────
  const handlePrescriptionUpload = async (file) => {
    const infinityCode = normalizeInfinityCodeValue(getInfinityCode(employeeData));
    if (!infinityCode) {
      setPrescriptionError(
        language === "ar" ? "لم يتم العثور على معرّف الموظف" : "Employee ID not found"
      );
      return;
    }

    // Mirrors the 5 MB / PDF-JPG-PNG limit enforced by the upload route, so the
    // user gets feedback without a round trip.
    const MAX_SIZE = 5 * 1024 * 1024;
    const allowedTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
      setPrescriptionError(
        language === "ar"
          ? "صيغة غير مدعومة. المسموح: PDF، JPG، PNG"
          : "Unsupported format. Allowed: PDF, JPG, PNG"
      );
      return;
    }

    if (file.size > MAX_SIZE) {
      setPrescriptionError(
        language === "ar"
          ? "حجم الملف أكبر من 5 ميجابايت"
          : "File is larger than 5 MB"
      );
      return;
    }

    setIsUploadingPrescription(true);
    setPrescriptionError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("infinityCode", infinityCode);

      // NOTE: no Content-Type header — the browser must set the multipart
      // boundary itself.
      const res = await apiFetch(`${apiBaseURL}/api/leaves/prescription`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.prescriptionPath) {
        throw new Error(data?.error || data?.message || t.request.prescriptionFailed);
      }

      setPrescription({ path: data.prescriptionPath, fileName: data.fileName || file.name });
    } catch (err) {
      setPrescription(null);
      setPrescriptionError(err?.message || t.request.prescriptionFailed);
    } finally {
      setIsUploadingPrescription(false);
    }
  };

  const handleRemovePrescription = () => {
    setPrescription(null);
    setPrescriptionError("");
  };

  // ─── إرسال طلب الإجازة ───────────────────────────────────────────────────────
  const handleSubmitRequest = async () => {
    const infinityCode = normalizeInfinityCodeValue(getInfinityCode(employeeData));
    if (!infinityCode) {
      setErrorMessage(
        language === "ar" ? "لم يتم العثور على معرّف الموظف" : "Employee ID not found"
      );
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (!isPartialDayMode && (leaveForm.type === "wfh" || leaveForm.type === "WFH")) {
        const hasPendingWFH = leaveHistory.some(
          (l) => (l.leaveType === "wfh" || l.type === "wfh" || l.leaveType === "WFH" || l.type === "WFH") && l.status === "pending"
        );
        if (hasPendingWFH) {
          showAlert(
            language === "ar" ? "تنبيه" : "Alert",
            language === "ar"
              ? "يوجد طلب عمل من المنزل قيد المراجعة بالفعل. لا يمكنك تقديم طلب آخر حتى تتم معالجة الطلب الحالي."
              : "A Work From Home request is already pending. You cannot submit another one until the current one is processed.",
            "warning"
          );
          setIsSubmitting(false);
          return;
        }
      }

      let payload;
      let url;

      const myAdmin =
        normalizeInfinityCodeValue(employeeData?.firstLineManagerInfinityCode) ||
        normalizeInfinityCodeValue(profile?.firstLineManagerInfinityCode) ||
        normalizeInfinityCodeValue(localStorage.getItem("firstLineManagerInfinityCode")) ||
        null;

      if (isPartialDayMode) {
        payload = {
          infinityCode,
          leaveType: "PARTIAL_DAY_LEAVE",
          date: partialDayForm.date,
          startDate: partialDayForm.date,
          endDate: partialDayForm.date,
          halfDayType: partialDayForm.halfDayType,
          reason: partialDayForm.reason,
          duration: 0.5,
          myAdmin,
          language, // Pass language to backend
        };
        url = `${apiBaseURL}/api/leaves`;
      } else {
        // Sick leave requires a prescription — block before hitting the API.
        if (leaveForm.type === "sick" && !prescription?.path) {
          showAlert(
            language === "ar" ? "تنبيه" : "Alert",
            t.request.prescriptionRequired,
            "warning"
          );
          setIsSubmitting(false);
          return;
        }

        payload = {
          infinityCode,
          leaveType: leaveForm.type,
          startDate: leaveForm.startDate,
          endDate: leaveForm.endDate,
          reason: leaveForm.reason,
          handover: leaveForm.handover || undefined,
          myAdmin,
          language, // Pass language to backend
          ...(prescription?.path && {
            isPrescriptionUploaded: true,
            prescriptionPath: prescription.path,
          }),
        };
        url = `${apiBaseURL}/api/leaves`;
      }

      const res = await apiFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(
          errData?.error || errData?.message ||
            (language === "ar" ? "فشل إرسال الطلب" : "Failed to submit request")
        );
      }

      setSuccessMessage(
        language === "ar"
          ? "تم إرسال طلب الإجازة بنجاح ✅"
          : "Leave request submitted successfully"
      );
      setShowRequestModal(false);
      setLeaveForm({ type: "", startDate: "", endDate: "", reason: "", handover: "" });
      setPartialDayForm({
        date: new Date().toISOString().split("T")[0],
        halfDayType: "",
        reason: "",
      });
      setIsPartialDayMode(false);
      setPrescription(null);
      setPrescriptionError("");
      await fetchAllData();
    } catch (err) {
      // Parse the backend error message and display an Alert Modal
      showAlert(
        language === "ar" ? "تنبيه" : "Alert",
        err?.message || (language === "ar" ? "حدث خطأ غير متوقع" : "An error occurred"),
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── فتح نافذة التفاصيل ───────────────────────────────────────────────────────
  const handleOpenDetails = (leave) => {
    setSelectedLeave(leave);
    setShowDetailsModal(true);
  };

  // ─── Notification deep-link (?leaveId=...) — open that leave's details ──────
  const [searchParams] = useSearchParams();
  const focusConsumedRef = useRef(false);
  useEffect(() => {
    const focusLeaveId = searchParams.get("leaveId");
    if (!focusLeaveId || focusConsumedRef.current) return;
    const list = Array.isArray(leaveHistory) ? leaveHistory : [];
    const leave = list.find((l) => Number(l.id) === Number(focusLeaveId));
    if (leave) {
      focusConsumedRef.current = true;
      handleOpenDetails(leave);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveHistory, searchParams]);

  // ─── Notification deep-link (?tab=holidays[&holidayId=...]) — open the
  // holidays tab and highlight the added/changed holiday ──────────────────────
  const focusHolidayId = searchParams.get("holidayId");
  const holidayTabConsumedRef = useRef(false);
  useEffect(() => {
    if (holidayTabConsumedRef.current) return;
    if (searchParams.get("tab") === "holidays" || focusHolidayId) {
      holidayTabConsumedRef.current = true;
      setActiveTab("holidays");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const holidayScrollConsumedRef = useRef(false);
  useEffect(() => {
    if (!focusHolidayId || holidayScrollConsumedRef.current) return;
    if (!Array.isArray(holidays) || holidays.length === 0) return;
    const match = holidays.find((h) => h.id != null && Number(h.id) === Number(focusHolidayId));
    if (match) {
      holidayScrollConsumedRef.current = true;
      // Let the holidays tab render first, then scroll to the card.
      setTimeout(() => {
        document.getElementById(`holiday-${match.id}`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holidays, focusHolidayId]);

  // ─── فتح نافذة الطلب ─────────────────────────────────────────────────────────
  const handleOpenRequest = (leaveType = null) => {
    // Any previously attached prescription belongs to the previous request.
    setPrescription(null);
    setPrescriptionError("");
    if (leaveType?.id === "partialDay") {
      setIsPartialDayMode(true);
      setLeaveForm({ type: "partialDay", startDate: "", endDate: "", reason: "", handover: "" });
    } else {
      setIsPartialDayMode(false);
      setLeaveForm({ type: leaveType?.id || "", startDate: "", endDate: "", reason: "", handover: "" });
    }
    setShowRequestModal(true);
  };

  // ─── Styles مشتركة ────────────────────────────────────────────────────────────
  const headerStyle = getHeaderStyle({ screenSize });

  // ✅ الكارد الأساسي يدعم الـ Dark/Light Mode بشكل صح
  // في الـ dark mode: خلفية شفافة مع blur
  // في الـ light mode: خلفية بيضاء مع ظل خفيف
  const cardStyle = {
    background: isDarkMode
      ? "rgba(255, 255, 255, 0.04)"
      : "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(25px)",
    WebkitBackdropFilter: "blur(25px)",
    border: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
    padding: spacing.padding,
    boxShadow: isDarkMode
      ? "0 0.5rem 2rem rgba(0, 0, 0, 0.25)"
      : "0 0.25rem 1.5rem rgba(0, 0, 0, 0.08)",
    marginBottom: screenSize === "xs" ? "20px" : "28px",
    transition: "all 0.3s ease",
  };

  const btnPrimary = getBtnPrimary({ isDarkMode, screenSize, fonts, theme });

  const toastStyle = (type) => getToastStyle({ type, language, fonts, theme });

  // ─── الإجازة القادمة (لو في إجازة رسمية في الـ 30 يوم الجاية) ──────────────
  const upcomingHoliday = holidays.find(
    (h) => h.daysUntil >= 0 && h.daysUntil <= 30
  );

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && isLoading) {
    const isRTL = language === "ar";
    return (
      <div style={{ direction: isRTL ? "rtl" : "ltr", fontFamily: "'Cairo', sans-serif", marginTop: "-30px" }}>
        <SkeletonStyles theme={isDarkMode ? "dark" : "light"} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل بيانات الإجازات..." : "Loading leave information..."}
          theme={isDarkMode ? "dark" : "light"}
          isRTL={isRTL}
        />

        {/* Header skeleton */}
        <div style={cardStyle}>
          <div style={headerStyle}>
            <div>
              <SkeletonBox width="220px" height="32px" style={{ marginBottom: "10px" }} />
              <SkeletonBox width="300px" height="18px" />
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <SkeletonBox width="120px" height="42px" style={{ borderRadius: "999px" }} />
              <SkeletonBox width="160px" height="42px" style={{ borderRadius: "999px" }} />
            </div>
          </div>
          <div style={{ height: "1px", background: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)", margin: `${spacing.margin} 0` }} />
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {TABS.map((tab) => (
              <SkeletonBox key={tab} width="110px" height="36px" style={{ borderRadius: "999px" }} />
            ))}
          </div>
        </div>

        {/* Stats skeleton */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: screenSize === "xs" || screenSize === "sm" ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            gap: screenSize === "xs" ? "12px" : "20px",
            marginBottom: screenSize === "xs" ? "20px" : "28px",
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatCard key={i} cardStyle={{ ...cardStyle, marginBottom: 0 }} />
          ))}
        </div>

        {/* Leave type cards skeleton */}
        <div style={getLeaveTypesGridStyle({ screenSize })}>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonBox key={i} width="100%" height="140px" style={{ borderRadius: "18px" }} />
          ))}
        </div>

        {/* Recent requests skeleton */}
        <div style={{ ...cardStyle, marginTop: screenSize === "xs" ? "20px" : "28px" }}>
          <SkeletonBox width="180px" height="24px" style={{ marginBottom: "20px" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[1, 2, 3].map((i) => (
              <SkeletonBox key={i} width="100%" height="58px" style={{ borderRadius: "12px" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <div
      style={{
        direction: language === "ar" ? "rtl" : "ltr",
        fontFamily: "'Cairo', sans-serif",
        marginTop: "-30px",
      }}
    >
      {/* ── Keyframes & Scrollbar ── */}
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(${language === "ar" ? "-" : ""}40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes shine { from { left: -100%; } to { left: 200%; } }
        @keyframes spin  { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb {
          background: ${isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"};
          border-radius: 2px;
        }
      `}</style>

      {/* ══════════════════════════════════════════════════════
          TOAST MESSAGES — رسائل النجاح والخطأ
          ══════════════════════════════════════════════════════ */}
      {successMessage && (
        <div style={toastStyle("success")}>
          <Bell size={18} />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div style={toastStyle("error")}>
          <Bell size={18} />
          {getDisplayError(errorMessage, language)}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          HEADER CARD — بطاقة الهيدر + التابز في نفس الكارد
          ══════════════════════════════════════════════════════ */}
      <div style={cardStyle}>
        {/* ── عنوان الصفحة وأزرار الإجراءات ── */}
        <div style={headerStyle}>
          {/* العنوان */}
          <div>
            <h1
              style={{
                color: isDarkMode ? "#ffffff" : "#1a1a1a",
                fontSize: screenSize === "xs" ? fonts.title : "2rem",
                fontWeight: "800",
                margin: 0,
                fontFamily: "'Cairo', sans-serif",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              {t.pageTitle}
            </h1>
            <p
              style={{
                color: isDarkMode
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(26, 26, 26, 0.55)",
                fontSize: fonts.body,
                margin: "6px 0 0 0",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {t.pageSubtitle}
            </p>
          </div>

          {/* أزرار التحديث والطلب */}
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {/* زر التحديث */}
            <button
              style={{
                ...btnPrimary,
                background: isDarkMode
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.06)",
                color: isDarkMode ? "#ffffff" : "#1a1a1a",
                boxShadow: "none",
                border: isDarkMode
                  ? "1px solid rgba(255, 255, 255, 0.15)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              }}
              onClick={() => { fetchAllData(); fetchHolidays(); }}
              disabled={isLoading}
            >
              <RefreshCw
                size={16}
                style={{ animation: isLoading ? "spin 1s linear infinite" : "none" }}
              />
              {isLoading
                ? language === "ar" ? "جارٍ التحميل..." : "Loading..."
                : t.refresh}
            </button>

            {/* زر طلب إجازة جديدة */}
            <button style={btnPrimary} onClick={() => handleOpenRequest()}>
              <Plus size={18} />
              {t.request.submitRequest}
            </button>
          </div>
        </div>

        {/* ── فاصل بين الهيدر والتابز ── */}
        <div
          style={{
            height: "1px",
            background: isDarkMode
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.07)",
            margin: `${spacing.margin} 0`,
          }}
        />

        {/* ✅ التابز داخل نفس كارد الهيدر
            كل تاب له لون مختلف لما يكون active
            في dark mode: أزرار شفافة مع اللون الأبيض
            في light mode: خلفية رمادية فاتحة مع نص غامق */}
        <div
          className="nx-tabs-scroll"
          style={{
            display: "flex",
            gap: screenSize === "xs" ? "6px" : "10px",
            flexWrap: "wrap",
            overflowX: "auto",
            paddingBottom: "2px",
          }}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: screenSize === "xs" ? "8px 14px" : "9px 20px",
                  borderRadius: "999px",
                  border: isActive
                    ? "none"
                    : isDarkMode
                    ? "1px solid rgba(255,255,255,0.12)"
                    : "1px solid rgba(0,0,0,0.1)",
                  cursor: "pointer",
                  fontSize: screenSize === "xs" ? fonts.tiny : fonts.small,
                  fontWeight: "600",
                  fontFamily: "'Cairo', sans-serif",
                  transition: "all 0.25s ease",
                  // ── Active Tab ──
                  background: isActive
                    ? `linear-gradient(135deg, #2e7cba, #2e7cba)`
                    : isDarkMode
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.04)",
                  color: isActive
                    ? "#ffffff"
                    : isDarkMode
                    ? "rgba(255,255,255,0.65)"
                    : "rgba(26,26,26,0.65)",
                  // boxShadow: isActive
                  //   ? `0 4px 14px ${theme.colors.primary}45`
                  //   : "none",
                  whiteSpace: "nowrap",
                }}
              >
                {t.tabs?.[tab] || tab}
              </button>
            );
          })}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          STATS CARDS — بطاقات الإحصائيات
          ══════════════════════════════════════════════════════ */}
      {leaveStats.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              screenSize === "xs" || screenSize === "sm"
                ? "repeat(2, 1fr)"
                : "repeat(4, 1fr)",
            gap: screenSize === "xs" ? "12px" : "20px",
            marginBottom: screenSize === "xs" ? "20px" : "28px",
          }}
        >
          {leaveStats.map((stat, index) => {
            const StatIcon = stat.icon;
            return (
              <div
                key={index}
                style={{
                  // ✅ دعم Dark/Light بشكل صح في بطاقات الإحصائيات
                  background: isDarkMode
                    ? "rgba(255, 255, 255, 0.04)"
                    : "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(25px)",
                  WebkitBackdropFilter: "blur(25px)",
                  border: isDarkMode
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                  borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
                  padding: screenSize === "xs" ? "16px" : "24px",
                  boxShadow: isDarkMode
                    ? "0 0.5rem 2rem rgba(0,0,0,0.25)"
                    : "0 0.25rem 1.5rem rgba(0,0,0,0.08)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                }}
              >
                {/* أيقونة الإحصائية */}
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    background: `${stat.color}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                  }}
                >
                  <StatIcon size={22} color={stat.color} />
                </div>

                {/* القيمة */}
                <div
                  style={{
                    color: stat.color,
                    fontSize: screenSize === "xs" ? "1.5rem" : "2rem",
                    fontWeight: "800",
                    lineHeight: 1,
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {stat.value}
                </div>

                {/* التسمية */}
                <div
                  style={{
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    fontSize: fonts.small,
                    fontWeight: "600",
                    margin: "6px 0 4px",
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {stat.label}
                </div>

                {/* الـ Trend */}
                <div
                  style={{
                    color: isDarkMode
                      ? "rgba(255,255,255,0.5)"
                      : "rgba(26,26,26,0.5)",
                    fontSize: fonts.tiny,
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {stat.trend}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          LOADING STATE — حالة التحميل
          ══════════════════════════════════════════════════════ */}
      {isLoading && (
        <div
          style={{
            ...cardStyle,
            textAlign: "center",
            padding: "48px 24px",
          }}
        >
          <RefreshCw
            size={32}
            color="#2e7cba"
            style={{ animation: "spin 1s linear infinite", marginBottom: "16px" }}
          />
          <p
            style={{
              color: isDarkMode ? "rgba(255,255,255,0.6)" : "rgba(26,26,26,0.6)",
              fontSize: fonts.body,
              margin: 0,
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {language === "ar" ? "جارٍ تحميل بيانات الإجازات..." : "Loading leave data..."}
          </p>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          TAB: OVERVIEW — نظرة عامة
          ══════════════════════════════════════════════════════ */}
      {!isLoading && activeTab === "overview" && (
        <div>
          {/* بطاقات أنواع الإجازات */}
          <div style={getLeaveTypesGridStyle({ screenSize })}>
            {validLeaveTypes.map((lt) => (
              <LeaveTypeCard
                key={lt.id}
                leaveType={lt}
                onClick={() => handleOpenRequest(lt)}
                isDarkMode={isDarkMode}
                screenSize={screenSize}
                fonts={fonts}
                spacing={spacing}
                theme={theme}
                t={t}
              />
            ))}
          </div>

          {/* معاينة سريعة لآخر الطلبات */}
          {leaveHistory.length > 0 && (
            <div
              style={{
                ...cardStyle,
                marginTop: screenSize === "xs" ? "20px" : "28px",
              }}
            >
              {/* هيدر القسم */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    color: isDarkMode ? "#ffffff" : "#1a1a1a",
                    fontSize: fonts.subtitle,
                    fontWeight: "600",
                    margin: 0,
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {t.history.recentRequests}
                </h3>
                <button
                  style={{
                    background: "none",
                    border: "none",
                    color: "#2e7cba",
                    cursor: "pointer",
                    fontSize: fonts.small,
                    fontWeight: "600",
                    fontFamily: "'Cairo', sans-serif",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onClick={() => setActiveTab("history")}
                >
                  {language === "ar" ? "عرض الكل" : "View all"}
                  <ChevronRight
                    size={16}
                    style={{
                      transform: language === "ar" ? "rotate(180deg)" : "none",
                    }}
                  />
                </button>
              </div>

              {/* قائمة آخر 5 طلبات */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {leaveHistory.slice(0, 5).map((leave) => {
                  const statusColors = {
                    approved: theme.colors.success,
                    pending: theme.colors.warning,
                    rejected: theme.colors.danger,
                  };
                  const color = statusColors[leave.status] || theme.colors.info;
                  return (
                    <div
                      key={leave.id}
                      onClick={() => handleOpenDetails(leave)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        padding: "14px 16px",
                        borderRadius: "12px",
                        // ✅ خلفية عنصر القائمة حسب الوضع
                        background: isDarkMode
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(0,0,0,0.025)",
                        border: isDarkMode
                          ? "1px solid rgba(255,255,255,0.07)"
                          : "1px solid rgba(0,0,0,0.06)",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isDarkMode
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(0,0,0,0.025)";
                      }}
                    >
                      {/* نقطة ملونة حسب الحالة */}
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          background: color,
                          flexShrink: 0,
                        }}
                      />

                      {/* تفاصيل الطلب */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            color: isDarkMode ? "#ffffff" : "#1a1a1a",
                            fontWeight: "600",
                            fontSize: fonts.body,
                            margin: 0,
                            fontFamily: "'Cairo', sans-serif",
                          }}
                        >
                          {t.leaveTypes[leave.leaveType || leave.type] ||
                            leave.leaveType ||
                            leave.type}
                        </p>
                        <p
                          style={{
                            color: isDarkMode
                              ? "rgba(255,255,255,0.55)"
                              : "rgba(26,26,26,0.55)",
                            fontSize: fonts.small,
                            margin: "2px 0 0 0",
                            fontFamily: "'Cairo', sans-serif",
                          }}
                        >
                          {new Date(leave.startDate).toLocaleDateString(
                            language === "ar" ? "ar-EG" : "en-US",
                            { month: "short", day: "numeric" }
                          )}
                          {" – "}
                          {new Date(leave.endDate).toLocaleDateString(
                            language === "ar" ? "ar-EG" : "en-US",
                            { month: "short", day: "numeric" }
                          )}
                        </p>
                      </div>

                      {/* badge الحالة */}
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "6px",
                          background: `${color}18`,
                          color,
                          fontSize: fonts.tiny,
                          fontWeight: "700",
                          fontFamily: "'Cairo', sans-serif",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.status?.[leave.status] || leave.status}
                      </span>

                      <ChevronRight
                        size={16}
                        color={isDarkMode ? "rgba(255,255,255,0.4)" : "rgba(26,26,26,0.35)"}
                        style={{
                          transform: language === "ar" ? "rotate(180deg)" : "none",
                          flexShrink: 0,
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          TAB: HISTORY — سجل الإجازات
          ══════════════════════════════════════════════════════ */}
      {!isLoading && activeTab === "history" && (
        <LeaveHistoryTab
          leaveHistory={leaveHistory}
          leaveTypes={leaveTypes}
          expandedLeaveId={expandedLeaveId}
          setExpandedLeaveId={setExpandedLeaveId}
          isDarkMode={isDarkMode}
          screenSize={screenSize}
          fonts={fonts}
          spacing={spacing}
          theme={theme}
          language={language}
          t={t}
        />
      )}

      {/* ══════════════════════════════════════════════════════
          TAB: TEAM — تقويم الفريق
          ══════════════════════════════════════════════════════ */}
      {!isLoading && activeTab === "team" && (
        <TeamLeaveCalendar
          managerInfinityCode={managerInfinityCode}
          employeeImages={employeeImages}
          isDarkMode={isDarkMode}
          screenSize={screenSize}
          fonts={fonts}
          spacing={spacing}
          theme={theme}
          language={language}
          t={t}
        />
      )}

      {/* ══════════════════════════════════════════════════════
          TAB: HOLIDAYS — الإجازات الرسمية
          ══════════════════════════════════════════════════════ */}
      {!isLoading && activeTab === "holidays" && (
        <div style={cardStyle}>
          {/* عنوان قسم الإجازات الرسمية */}
          <h3
            style={{
              color: isDarkMode ? "#ffffff" : "#1a1a1a",
              fontSize: fonts.subtitle,
              fontWeight: "600",
              margin: `0 0 ${spacing.margin} 0`,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            <Gift size={24} color={theme.colors.primary} />
            {t.holidays?.title ||
              (language === "ar" ? "الإجازات الرسمية" : "Public Holidays")}
          </h3>

          {holidays.length === 0 ? (
            <p
              style={{
                textAlign: "center",
                color: isDarkMode ? "rgba(255,255,255,0.5)" : "rgba(26,26,26,0.5)",
                padding: "40px 0",
                fontFamily: "'Cairo', sans-serif",
                fontSize: fonts.body,
              }}
            >
              {language === "ar" ? "لا توجد إجازات رسمية" : "No holidays found"}
            </p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  screenSize === "xs" || screenSize === "sm"
                    ? "1fr"
                    : screenSize === "md"
                    ? "repeat(2, 1fr)"
                    : "repeat(3, 1fr)",
                gap: screenSize === "xs" ? "12px" : "20px",
              }}
            >
              {holidays.map((holiday, i) => (
                <div
                  key={`${holiday.date}-${i}`}
                  id={holiday.id != null ? `holiday-${holiday.id}` : undefined}
                  style={
                    holiday.id != null && Number(focusHolidayId) === Number(holiday.id)
                      ? { outline: "2px solid #3b82f6", outlineOffset: "3px", borderRadius: "16px" }
                      : undefined
                  }
                >
                  <HolidayCard
                    holiday={holiday}
                    isDarkMode={isDarkMode}
                    screenSize={screenSize}
                    fonts={fonts}
                    spacing={spacing}
                    theme={theme}
                    language={language}
                    t={t}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MODALS — النوافذ المنبثقة
          ══════════════════════════════════════════════════════ */}
      <RequestModal
        showRequestModal={showRequestModal}
        setShowRequestModal={setShowRequestModal}
        isPartialDayMode={isPartialDayMode}
        setIsPartialDayMode={setIsPartialDayMode}
        leaveForm={leaveForm}
        setLeaveForm={setLeaveForm}
        partialDayForm={partialDayForm}
        setPartialDayForm={setPartialDayForm}
        leaveTypes={leaveTypes}
        leaveStats={leaveStats}
        isSubmitting={isSubmitting}
        handleSubmitRequest={handleSubmitRequest}
        prescription={prescription}
        isUploadingPrescription={isUploadingPrescription}
        prescriptionError={prescriptionError}
        handlePrescriptionUpload={handlePrescriptionUpload}
        handleRemovePrescription={handleRemovePrescription}
        isDarkMode={isDarkMode}
        screenSize={screenSize}
        fonts={fonts}
        spacing={spacing}
        theme={theme}
        language={language}
        t={t}
      />

      <DetailsModal
        showDetailsModal={showDetailsModal}
        setShowDetailsModal={setShowDetailsModal}
        selectedLeave={selectedLeave}
        isDarkMode={isDarkMode}
        screenSize={screenSize}
        fonts={fonts}
        spacing={spacing}
        theme={theme}
        language={language}
        t={t}
      />

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={closeAlert}
        theme={isDarkMode ? "dark" : "light"}
        language={language}
      />
    </div>
  );
};

export default LeavePage;